import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useTestTag from '../../common/hooks/use-test-tag';
import type { OdinKey } from '../../common/types/odin-key.type';
import HelpComponent from '../help-component/help.component';
import { DataProxyInstanceService } from './data-proxy-instance.service';
import { DynamicFieldService } from './dynamic-field.service';
import { DynamicFormRequiredType } from './dynamic-form.enums';
import type {
  DynamicFieldDataProps,
  DynamicFormFieldPermissions,
  DynamicFormFieldType,
  FieldValueConverter,
  HfDynamicFieldComponentProps,
} from './dynamic-form.interfaces';
import { applyExtraProperties } from './dynamic-form-common';

// 'CUSTOM' covers most record fields: web moves the real type into `customFieldType` and sets
// `fieldType: 'CUSTOM'` for every field it renders with a custom component, so 'CUSTOM' is the only spelling those fields arrive as.
// The consequence is that every custom field receives the hint as `placeholder`, but components that take no placeholder simply ignore it.
const HINT_AS_PLACEHOLDER_FIELDS: ReadonlyArray<DynamicFormFieldType> = [
  'TEXTFIELD',
  'TEXTAREA',
  'COMBOBOX',
  'INTFIELD',
  'DOUBLEFIELD',
  'RICHTEXTAREA',
  'HISTORY',
  'CUSTOM',
];

/** Whether a field of this type renders its configured hint message as the input placeholder. */
export const usesHintAsPlaceholder = (fieldType: DynamicFormFieldType): boolean => {
  return HINT_AS_PLACEHOLDER_FIELDS.includes(fieldType);
};

export interface DynamicFieldProps {
  id: OdinKey;
  dynamicFormId: string;
  value?: unknown;
  fieldName: string;
  fieldType: DynamicFormFieldType;
  caption: string;
  required?: DynamicFormRequiredType;
  error?: string;
  dynamicProperties?: Record<string, unknown>;
  readOnly?: boolean;
  customFieldType?: string;
  helpMessage?: string;
  hintMessage?: string;
  fieldValueConverter?: FieldValueConverter;
  permissions?: DynamicFormFieldPermissions;
  hidden?: boolean;
  scrollId?: string;
  labelStyles?: string;
  resetField: (value?: unknown) => void;
  setFieldValue: (value?: unknown) => void;
}

const DynamicField = ({
  id,
  dynamicFormId,
  value,
  fieldName,
  fieldType,
  caption,
  required = DynamicFormRequiredType.False,
  error,
  dynamicProperties,
  readOnly,
  customFieldType,
  helpMessage,
  hintMessage,
  fieldValueConverter,
  permissions,
  hidden,
  scrollId,
  labelStyles,
  resetField,
  setFieldValue,
}: DynamicFieldProps) => {
  // Stabilise callback props via refs so React.memo can skip them in comparisons.
  // The parent recreates these closures every render, but the latest version is
  // always available through the ref without triggering a re-render.
  const resetFieldRef = useRef(resetField);
  const setFieldValueRef = useRef(setFieldValue);
  resetFieldRef.current = resetField;
  setFieldValueRef.current = setFieldValue;

  const fieldService = useRef(DynamicFieldService.getServiceInstance());
  const dataProxyService = useRef(DataProxyInstanceService.getServiceInstance(dynamicFormId));
  const Component = useMemo(
    () => fieldService.current.getFieldComponent(fieldType, customFieldType),
    [fieldType, customFieldType]
  );

  const [isHidden, setIsHidden] = useState(hidden);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Only trigger on hidden prop change, not on internal state or callbacks
  useEffect(() => {
    if (isHidden !== hidden) {
      if (hidden === true) {
        // reset field if it becomes hidden to prevent it being marked as dirty or sent with the record data
        resetFieldRef.current(value);
      } else if (hidden === false) {
        // mark it as dirty if it becomes visible so it's sent with the record data
        resetFieldRef.current(null); // we have to reset it to null first,
        setFieldValueRef.current(value); // then set the value — shouldDirty is controlled by the parent
      }

      setIsHidden(hidden);
    }
  }, [hidden]);

  let dynamicProps: Partial<HfDynamicFieldComponentProps<unknown, unknown> & DynamicFieldDataProps> = {
    ...dynamicProperties,
    value,
    label: caption,
    labelStyles: labelStyles,
    required,
    error,
    readOnly,
    permissions,
    id,
    name: fieldName,
    placeholder: usesHintAsPlaceholder(fieldType) ? hintMessage : undefined,
    fieldValueConverter,
  };

  dynamicProps = applyExtraProperties(dynamicProps);

  // TODO: Determine if there is a way for the user to pass the checkboxes value and if so remove this code
  if (fieldType === 'CHECKBOX') {
    // checking a checkbox should return the value of true
    dynamicProps['value'] = true;
  } else if (['TEXTFIELD', 'INTFIELD', 'DOUBLEFIELD'].includes(fieldType)) {
    dynamicProps['enableDebounce'] = false;
  }

  // Stable ref for fieldValueConverter so useCallback doesn't depend on the
  // function reference (which may change on every render from the parent).
  const fieldValueConverterRef = useRef(fieldValueConverter);
  fieldValueConverterRef.current = fieldValueConverter;

  const onChange = useCallback(
    (value: unknown) => {
      const dataProxy = dataProxyService.current.dataProxy;
      if (dataProxy) {
        const converter = fieldValueConverterRef.current;
        dataProxy[fieldName] = converter ? converter(value) : value;
      }
    },
    [fieldName]
  );

  dynamicProps = {
    ...dynamicProps,
    onChange,
  };

  const tagCreator = useTestTag(caption || '');

  if (Component && dynamicProps) {
    return (
      <div className={isHidden ? 'hidden' : 'mb-3'}>
        <div className="relative flex w-full items-center" ref={tagCreator('dynamic-field')}>
          <div id={scrollId} data-skip-scroll-element={true} className="min-w-0 grow">
            <Component {...dynamicProps} />
          </div>
          {helpMessage && <HelpComponent description={helpMessage} className="help-component-default" />}
        </div>
      </div>
    );
  }

  return null;
};

/**
 * Shallow-compare two plain objects by own enumerable keys.
 * Returns true when both sides have the same keys with identical (===) values.
 */
export function shallowEqual(a?: Record<string, unknown>, b?: Record<string, unknown>): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;
  for (let i = 0, len = keysA.length; i < len; i++) {
    const key = keysA[i];
    // biome-ignore lint/suspicious/noPrototypeBuiltins: Object.hasOwn requires es2022 lib; bowtie/training target older libs
    if (!Object.prototype.hasOwnProperty.call(b, key) || a[key] !== b[key]) return false;
  }
  return true;
}

/**
 * Custom comparator for React.memo. Skips `resetField` and `setFieldValue`
 * (stabilised via refs inside the component). Compares `dynamicProperties`
 * and `permissions` with a shallow-equal check because the parent recreates
 * these objects every render even when the values inside are unchanged.
 * `value` uses `===` which is safe because the DataProxy has no `get` trap —
 * unchanged fields return the same object reference across renders.
 * `fieldValueConverter` uses `===` because the parent derives it from
 * field settings (a stable reference); the ref inside the component is only
 * needed for `useCallback` closure stability, not for the comparator.
 *
 * **Important:** This list must stay in sync with {@link DynamicFieldProps}.
 * If a new prop is added to the interface, add a comparison here or the
 * field will render with stale data.
 */
export function areDynamicFieldPropsEqual(prev: DynamicFieldProps, next: DynamicFieldProps): boolean {
  return (
    prev.id === next.id &&
    prev.dynamicFormId === next.dynamicFormId &&
    prev.value === next.value &&
    prev.fieldName === next.fieldName &&
    prev.fieldType === next.fieldType &&
    prev.caption === next.caption &&
    prev.required === next.required &&
    prev.error === next.error &&
    prev.readOnly === next.readOnly &&
    prev.customFieldType === next.customFieldType &&
    prev.helpMessage === next.helpMessage &&
    prev.hintMessage === next.hintMessage &&
    prev.fieldValueConverter === next.fieldValueConverter &&
    shallowEqual(
      prev.permissions as Record<string, unknown> | undefined,
      next.permissions as Record<string, unknown> | undefined
    ) &&
    prev.hidden === next.hidden &&
    prev.scrollId === next.scrollId &&
    prev.labelStyles === next.labelStyles &&
    shallowEqual(prev.dynamicProperties, next.dynamicProperties)
  );
}

export default memo(DynamicField, areDynamicFieldPropsEqual);
